import { ErrorMessage } from '@client/new/components/Label';
import { Option } from '@client/new/helpers/types';
import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { fontFamily, space } from '@design-system/tokens';
import { bgColorsVars } from '@design-system/vars.css';
import { MenuItem, Select } from '@mui/material';
import { forwardRef } from 'react';

export type SelectProps<T extends string | number> = {
  options: Array<Option<T>>;
  value?: T | null;
  name: string;
  onChange: (v: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  width?: number;
  label?: string;
};

export const MenuSelect = forwardRef(function MenuSelect<
  T extends string | number,
>(
  {
    error,
    width,
    label,
    ...p
  }: SelectProps<T> &
    React.DetailedHTMLProps<
      React.InputHTMLAttributes<HTMLInputElement>,
      HTMLInputElement
    >,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  return (
    <Col style={{ gap: 0, ...(width ? { width: width + 'px' } : {}) }}>
      {label && (
        <Typography
          variant="body"
          size="medium"
          fontWeight="medium"
          style={{ marginBottom: space.s }}
        >
          {label}
        </Typography>
      )}
      <Row gap="s">
        <Select
          error={!!error}
          ref={ref}
          displayEmpty
          value={p.value ?? null}
          onChange={(e) => p.onChange(e as any)}
          renderValue={(value: T | null) => {
            if (!value) return 'Select';
            const option = p.options.find((i) => i.value === value);
            return option?.label ?? 'Select';
          }}
          sx={{
            width: '100%',
            height: '40px',
            borderWidth: '2px',
            marginTop: '1px',
            fontFamily: fontFamily.visuelt,
            borderRadius: '10px',
            boxShadow: 'none !important',
            padding: '6px 16px',

            borderColor: error ? bgColorsVars['ERROR-100'] : '#e4e4e4',
            '&.Mui-error': {
              fontFamily: fontFamily.visuelt,
              borderColor: bgColorsVars['ERROR-100'],
            },
            '& .MuiSelect-select': {
              fontSize: '16px',
            },
          }}
        >
          {p.options.map(({ value, label }, i) => (
            <MenuItem key={i} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Row>
      {error ? (
        <div style={{ marginTop: 8 }}>
          <ErrorMessage content={error} />
        </div>
      ) : null}
    </Col>
  );
});
